import { Injectable } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { FnAppSessionService } from 'foundation';

interface ChartDataPoint {
  avg: number;
  hostSet: string;
  count: number;
  time: string; 
  schema?: string; 
}

@Injectable({
  providedIn: 'root'
})
export class AppRestartService {
  constructor(private apSessionSvcs: FnAppSessionService) {}

  createTimeWiseGraph(container: HTMLElement, data: ChartDataPoint[], title: string, onBarClick: (hostSet: string) => void): void {
    container.innerHTML = '';
    const chartDiv = document.createElement('div');
    chartDiv.style.width = '100%';
    chartDiv.style.height = '400px';
    container.appendChild(chartDiv);

    const root = am5.Root.new(chartDiv);
    if (root._logo) {
      root._logo.dispose();
    }

    root.container.set('background', am5.Rectangle.new(root, {
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0x121418 : 0xf3f1f1),
      fillOpacity: 1
    }));

    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        layout: root.verticalLayout,
        paddingLeft: 10,
        paddingRight: 20,
      })
    );

    chart.children.unshift(
      am5.Label.new(root, {
        text: title,
        fontSize: 18,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        textAlign: 'center',
        x: am5.percent(50),
        centerX: am5.percent(50),
        fontWeight: 'bold',
      })
    );

    const xAxisRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 50 }); 
    xAxisRenderer.grid.template.setAll({ visible: false });
    xAxisRenderer.labels.template.setAll({
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
    });
    xAxisRenderer.setAll({
      stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      strokeOpacity: 1,
      strokeWidth: 1,
    });
    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        renderer: xAxisRenderer,
        categoryField: 'time',
        tooltip: am5.Tooltip.new(root, { labelText: '{category}' }),
      })
    );
    xAxis.data.setAll(data);
    xAxis.children.push(
      am5.Label.new(root, {
        text: 'Time',
        fontSize: 14,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        x: am5.percent(50),
        centerX: am5.percent(50),
        y: am5.percent(100),
        centerY: am5.percent(100),
        paddingTop: 5,
      })
    );

    const yAxisLeftRenderer = am5xy.AxisRendererY.new(root, { opposite: false });
    yAxisLeftRenderer.grid.template.setAll({ visible: false });
    yAxisLeftRenderer.setAll({
      stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      strokeOpacity: 1,
      strokeWidth: 1,
    });
    yAxisLeftRenderer.labels.template.setAll({
      fontSize: 12,
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      paddingRight: 10,
    });
    const yAxisLeft = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: yAxisLeftRenderer,
        tooltip: am5.Tooltip.new(root, {}),
        min: 0,
        extraMax: 0.1,
        numberFormat: '#',
      })
    );
    yAxisLeft.children.unshift(
      am5.Label.new(root, {
        text: 'Count',
        fontSize: 14,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        rotation: -90,
        x: -2,
        y: am5.percent(50),
        centerY: am5.percent(50),
        centerX: am5.percent(50),
      })
    );

    const yAxisRightRenderer = am5xy.AxisRendererY.new(root, { opposite: true });
    yAxisRightRenderer.grid.template.setAll({ visible: false });
    yAxisRightRenderer.setAll({
      stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFF3333 : 0x000000),
      strokeOpacity: 1,
      strokeWidth: 1,
    });
    yAxisRightRenderer.labels.template.setAll({
      fontSize: 12,
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      paddingLeft: 5,
    });
    const yAxisRight = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: yAxisRightRenderer,
        tooltip: am5.Tooltip.new(root, {}),
        min: 0,
        extraMax: 0.1,
        numberFormat: '#.##',
      })
    );
    yAxisRight.children.push(
      am5.Label.new(root, {
        text: 'Average (sec)',
        fontSize: 14,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        rotation: -90,
        x: 40,
        y: am5.percent(50),
        centerY: am5.percent(50),
        centerX: am5.percent(50),
      })
    );

    const barSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Count',
        xAxis: xAxis,
        yAxis: yAxisLeft,
        valueYField: 'count',
        categoryXField: 'time',
        tooltip: am5.Tooltip.new(root, { labelText: 'Count: {valueY}' }),
      })
    );
    barSeries.data.setAll(data);
    barSeries.columns.template.setAll({
      maxWidth: 50,
      width: am5.percent(80),
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFF00 : 0x87CEFA),
      strokeOpacity: 0,
    });

    barSeries.columns.template.adapters.add('fill', (fill, target) => {
      const dataItem = target.dataItem as am5.DataItem<am5xy.IColumnSeriesDataItem>;
      if (dataItem) {
        const dataContext = dataItem.dataContext as any;
        if (dataContext.avg > 60) {
          return am5.color(0xFF3333); 
        }
      }
      return fill;
    });

    barSeries.columns.template.adapters.add('fill', (fill, target) => {
      const dataItem = target.dataItem as am5.DataItem<am5xy.IColumnSeriesDataItem>;
      if (dataItem) {
        const dataContext = dataItem.dataContext as any;
        if (dataContext.avg < 60) {
          return am5.color(0x00FF00); 
        }
      }
      return fill;
    });

    barSeries.bullets.push(() =>
      am5.Bullet.new(root, {
        locationY: 1,
        sprite: am5.Label.new(root, {
          text: '{valueY}',
          fontSize: 12,
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0x59ABF6 : 0x000000),
          centerX: am5.percent(50),
          centerY: am5.percent(50),
          dy: -10,
          populateText: true,
        }),
      })
    );

    barSeries.columns.template.events.on('click', (ev) => {
      const dataItem = ev.target.dataItem;
      if (dataItem && dataItem.dataContext && (dataItem.dataContext as any).hostSet) {
        onBarClick((dataItem.dataContext as any).hostSet);
      }
    });

    const lineSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: 'Average',
        xAxis: xAxis,
        yAxis: yAxisRight,
        valueYField: 'avg',
        categoryXField: 'time',
        stroke: am5.color(0xFF0000),
        tooltip: am5.Tooltip.new(root, { labelText: 'Average: {valueY}' }),
      })
    );
    lineSeries.data.setAll(data);
    lineSeries.strokes.template.setAll({
      strokeWidth: 3,
      stroke: am5.color(0x000000),
    });
    lineSeries.bullets.push(() =>
      am5.Bullet.new(root, {
        locationY: 0.5,
        sprite: am5.Label.new(root, {
          text: '{valueY}',
          fontSize: 12,
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xf50000 : 0x000000),
          centerX: am5.percent(50),
          centerY: am5.percent(-20),
          populateText: true,
        }),
      })
    );
    lineSeries.bullets.push(() =>
      am5.Bullet.new(root, {
        sprite: am5.Circle.new(root, {
          radius: 5,
          fill: am5.color(0xFF0000),
          stroke: am5.color(0xFFFFFF),
          strokeWidth: 2,
        }),
      })
    );

    const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {}));
    cursor.lineX.set('visible', false);
    cursor.lineY.set('visible', false);

    const countMax = Math.max(...data.map(d => d.count));
    const countMin = Math.min(...data.map(d => d.count));
    const avgMax = Math.max(...data.map(d => d.avg));
    const avgMin = Math.min(...data.map(d => d.avg));
    chart.children.push(
      am5.Label.new(root, {
        text: `[#00FF00]■[/] Count:- Max: ${countMax}, Min: ${countMin}    [#FF0000]—[/] Average:- Max: ${avgMax.toFixed(2)}, Min: ${avgMin.toFixed(2)}`,
        fontSize: 14,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x333333),
        textAlign: 'left',
        x: am5.percent(0),
        centerX: am5.percent(0),
        paddingLeft: 10,
        paddingTop: 10,
        paddingBottom: 10,
      })
    );
  }

  createHostWiseGraph(container: HTMLElement, data: any[], title: string): void {
    container.innerHTML = '';
    const chartDiv = document.createElement('div');
    chartDiv.style.width = '100%';
    chartDiv.style.height = '400px';
    container.appendChild(chartDiv);

    const root = am5.Root.new(chartDiv);
    if (root._logo) {
      root._logo.dispose();
    }

    root.container.set('background', am5.Rectangle.new(root, {
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0x121418 : 0xf3f1f1),
      fillOpacity: 1
    }));

    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        layout: root.verticalLayout,
        paddingLeft: 10,
        paddingRight: 20,
      })
    );

    chart.children.unshift(
      am5.Label.new(root, {
        text: title,
        fontSize: 18,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        textAlign: 'center',
        x: am5.percent(50),
        centerX: am5.percent(50),
        fontWeight: 'bold',
      })
    );

    const xAxisRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });
    xAxisRenderer.grid.template.setAll({ visible: false });
    xAxisRenderer.labels.template.setAll({
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      rotation: -45,
      textAlign: 'right',
    });
    xAxisRenderer.setAll({
      stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      strokeOpacity: 1,
      strokeWidth: 1,
    });
    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        renderer: xAxisRenderer,
        categoryField: 'hostName',
        tooltip: am5.Tooltip.new(root, {}),
      })
    );
    xAxis.data.setAll(data);
    xAxis.children.push(
      am5.Label.new(root, {
        text: 'Host Name',
        fontSize: 14,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        x: am5.percent(50),
        centerX: am5.percent(50),
        y: am5.percent(100),
        centerY: am5.percent(100),
        paddingTop: 5,
      })
    );

    const yAxisLeftRenderer = am5xy.AxisRendererY.new(root, { opposite: false });
    yAxisLeftRenderer.grid.template.setAll({ visible: false });
    yAxisLeftRenderer.setAll({
      stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      strokeOpacity: 1,
      strokeWidth: 1,
    });
    yAxisLeftRenderer.labels.template.setAll({
      fontSize: 12,
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      paddingRight: 10,
    });
    const yAxisLeft = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: yAxisLeftRenderer,
        tooltip: am5.Tooltip.new(root, {}),
        min: 0,
        extraMax: 0.1,
        numberFormat: '#',
      })
    );
    yAxisLeft.children.unshift(
      am5.Label.new(root, {
        text: 'Count',
        fontSize: 14,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        rotation: -90,
        x: -2,
        y: am5.percent(50),
        centerY: am5.percent(50),
        centerX: am5.percent(50),
      })
    );

    const yAxisRightRenderer = am5xy.AxisRendererY.new(root, { opposite: true });
    yAxisRightRenderer.grid.template.setAll({ visible: false });
    yAxisRightRenderer.setAll({
      stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFF3333 : 0x000000),
      strokeOpacity: 1,
      strokeWidth: 1,
    });
    yAxisRightRenderer.labels.template.setAll({
      fontSize: 12,
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      paddingLeft: 5,
    });
    const yAxisRight = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: yAxisRightRenderer,
        tooltip: am5.Tooltip.new(root, {}),
        min: 0,
        extraMax: 0.1,
        numberFormat: '#.##',
      })
    );
    yAxisRight.children.push(
      am5.Label.new(root, {
        text: 'Average (sec)',
        fontSize: 14,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        rotation: -90,
        x: 40,
        y: am5.percent(50),
        centerY: am5.percent(50),
        centerX: am5.percent(50),
      })
    );

    const barSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Count',
        xAxis: xAxis,
        yAxis: yAxisLeft,
        valueYField: 'count',
        categoryXField: 'hostName',
        tooltip: am5.Tooltip.new(root, { labelText: 'Count: {valueY}' }),
      })
    );
    barSeries.data.setAll(data);
    barSeries.columns.template.setAll({
      maxWidth: 50,
      width: am5.percent(80),
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFF00 : 0x87CEFA),
      strokeOpacity: 0,
    });
    barSeries.bullets.push(() =>
      am5.Bullet.new(root, {
        locationY: 1,
        sprite: am5.Label.new(root, {
          text: '{valueY}',
          fontSize: 12,
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0x59ABF6 : 0x000000),
          centerX: am5.percent(50),
          centerY: am5.percent(50),
          dy: -10,
          populateText: true,
        }),
      })
    );

    const lineSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: 'Average',
        xAxis: xAxis,
        yAxis: yAxisRight,
        valueYField: 'avg',
        categoryXField: 'hostName',
        stroke: am5.color(0xFF0000),
        tooltip: am5.Tooltip.new(root, { labelText: 'Average: {valueY}' }),
      })
    );
    lineSeries.data.setAll(data);
    lineSeries.strokes.template.setAll({
      strokeWidth: 3,
      stroke: am5.color(0x000000),
    });
    lineSeries.bullets.push(() =>
      am5.Bullet.new(root, {
        locationY: 0.5,
        sprite: am5.Label.new(root, {
          text: '{valueY}',
          fontSize: 12,
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xf50000 : 0x000000),
          centerX: am5.percent(50),
          centerY: am5.percent(-20),
          populateText: true,
        }),
      })
    );
    lineSeries.bullets.push(() =>
      am5.Bullet.new(root, {
        sprite: am5.Circle.new(root, {
          radius: 5,
          fill: am5.color(0xFF0000),
          stroke: am5.color(0xFFFFFF),
          strokeWidth: 2,
        }),
      })
    );

    const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {}));
    cursor.lineX.set('visible', false);
    cursor.lineY.set('visible', false);

    const countMax = Math.max(...data.map(d => d.count));
    const countMin = Math.min(...data.map(d => d.count));
    const avgMax = Math.max(...data.map(d => d.avg));
    const avgMin = Math.min(...data.map(d => d.avg));
    chart.children.push(
      am5.Label.new(root, {
        text: `[#f8625d]■[/] Count:- Max: ${countMax}, Min: ${countMin}    [#FF0000]—[/] Average:- Max: ${avgMax.toFixed(2)}, Min: ${avgMin.toFixed(2)}`,
        fontSize: 14,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x333333),
        textAlign: 'left',
        x: am5.percent(0),
        centerX: am5.percent(0),
        paddingLeft: 10,
        paddingTop: 10,
        paddingBottom: 10,
      })
    );
  }

  createModalChart(container: HTMLElement, data: { hostName: string; count: number }[]): void {
    container.innerHTML = '';
    const chartDiv = document.createElement('div');
    chartDiv.style.width = '100%';
    chartDiv.style.height = '300px';
    container.appendChild(chartDiv);

    const root = am5.Root.new(chartDiv);
    if (root._logo) {
      root._logo.dispose();
    }

    root.container.set('background', am5.Rectangle.new(root, {
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0x121418 : 0xf3f1f1),
      fillOpacity: 1
    }));

    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        layout: root.verticalLayout,
        paddingLeft: 10,
        paddingRight: 20,
      })
    );

    chart.children.unshift(
      am5.Label.new(root, {
        text: 'Host Counts',
        fontSize: 18,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        textAlign: 'center',
        x: am5.percent(50),
        centerX: am5.percent(50),
        fontWeight: 'bold',
      })
    );

    const xAxisRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });
    xAxisRenderer.grid.template.setAll({ visible: false });
    xAxisRenderer.labels.template.setAll({
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      rotation: -45,
      textAlign: 'right',
    });
    xAxisRenderer.setAll({
      stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      strokeOpacity: 1,
      strokeWidth: 1,
    });
    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        renderer: xAxisRenderer,
        categoryField: 'hostName',
        tooltip: am5.Tooltip.new(root, {}),
      })
    );
    xAxis.data.setAll(data);
    xAxis.children.push(
      am5.Label.new(root, {
        text: 'Host Name',
        fontSize: 14,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        x: am5.percent(50),
        centerX: am5.percent(50),
        y: am5.percent(100),
        centerY: am5.percent(100),
        paddingTop: 5,
      })
    );

    const yAxisRenderer = am5xy.AxisRendererY.new(root, { opposite: false });
    yAxisRenderer.grid.template.setAll({ visible: false });
    yAxisRenderer.setAll({
      stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      strokeOpacity: 1,
      strokeWidth: 1,
    });
    yAxisRenderer.labels.template.setAll({
      fontSize: 12,
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      paddingRight: 10,
    });
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: yAxisRenderer,
        tooltip: am5.Tooltip.new(root, {}),
        min: 0,
        extraMax: 0.1,
        numberFormat: '#',
      })
    );
    yAxis.children.unshift(
      am5.Label.new(root, {
        text: 'Count',
        fontSize: 14,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        rotation: -90,
        x: -2,
        y: am5.percent(50),
        centerY: am5.percent(50),
        centerX: am5.percent(50),
      })
    );

    const barSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Count',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'count',
        categoryXField: 'hostName',
        tooltip: am5.Tooltip.new(root, { labelText: 'Count: {valueY}' }),
      })
    );
    barSeries.data.setAll(data);
    barSeries.columns.template.setAll({
      maxWidth: 50,
      width: am5.percent(80),
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFF00 : 0x87CEFA),
      strokeOpacity: 0,
    });
    barSeries.bullets.push(() =>
      am5.Bullet.new(root, {
        locationY: 1,
        sprite: am5.Label.new(root, {
          text: '{valueY}',
          fontSize: 12,
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0x59ABF6 : 0x000000),
          centerX: am5.percent(50),
          centerY: am5.percent(50),
          dy: -10,
          populateText: true,
        }),
      })
    );

    const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {}));
    cursor.lineX.set('visible', false);
    cursor.lineY.set('visible', false);

    const countMax = Math.max(...data.map(d => d.count));
    const countMin = Math.min(...data.map(d => d.count));
    chart.children.push(
      am5.Label.new(root, {
        text: `[#f8625d]■[/] Count:- Max: ${countMax}, Min: ${countMin}`,
        fontSize: 14,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x333333),
        textAlign: 'left',
        x: am5.percent(0),
        centerX: am5.percent(0),
        paddingLeft: 10,
        paddingTop: 10,
        paddingBottom: 10,
      })
    );
  }
}